import moment from 'moment';

class WebSocketService {
  static instance = null;
  callbacks = {
    messages: [],
    new_message: [],
    error: [],
    init_chat: [],
    connection_status: [],
    unread_count: [],
    update_unread_count: [] // Added the new callback type
  };


  static getInstance() {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  constructor() {
  this.socketRef = null;
  this.connectionStatus = 'disconnected';
  this.reconnectAttempts = 0;
  this.maxReconnectAttempts = 5;
  this.reconnectDelay = 3000;
  this._reconnectTimeout = null;
}


  connect(context) {
    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout);
      this._reconnectTimeout = null;
    }

    if (this.socketRef && this.socketRef.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      this.updateConnectionStatus('connected');
      return;
    }

    const wsPrefix = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const port = window.location.port;
    // Same-origin via nginx /ws/ → Daphne (see nginx-local.conf). Direct :8003 only off nginx.
    const socketBase = (!port || port === '80' || port === '443')
      ? `${wsPrefix}://${window.location.host}`
      : `${wsPrefix}://${window.location.hostname}:8003`;

    const path = `${socketBase}/ws/chat/chatroom/`;
    console.log('Connecting to WebSocket:', path);

    this.socketRef = new WebSocket(path);
    this.updateConnectionStatus('connecting');

    this.socketRef.onopen = () => {
      console.log("WebSocket connected.");
        this.updateConnectionStatus('connected');
      this.reconnectAttempts = 0;
    };

    this.socketRef.onmessage = (e) => {
      this.socketNewMessage(e.data);
    };

    this.socketRef.onerror = (e) => {
      console.error('WebSocket error:', e);
      this.notifyCallbacks('error', { message: 'WebSocket error occurred' });
    };

    this.socketRef.onclose = () => {
      console.log("WebSocket closed.");
      this.connectionStatus = 'disconnected';

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        console.log(`Attempting reconnect #${this.reconnectAttempts + 1}`);
        this._reconnectTimeout = setTimeout(() => {
          this.reconnectAttempts++;
          this.connect(context);
        }, this.reconnectDelay);
      } else {
        this.updateConnectionStatus('disconnected');
        console.error("Max reconnect attempts reached.");
      }
    };
  }

  socketNewMessage(data) {
    try {
      const parsedData = JSON.parse(data);
      const command = parsedData.command;

      if (command === 'fetch_messages') {
        this.notifyCallbacks('messages', {
          messages: parsedData.messages
        });
      } else if (command === 'new_message') {
        this.notifyCallbacks('new_message', parsedData.message);
      } else if (command === 'error') {
        this.notifyCallbacks('error', { message: parsedData.error });
      } else if (command === 'init_chat') {
        this.notifyCallbacks('init_chat', parsedData);
      } else if (command === 'unread_count') {
        this.notifyCallbacks('unread_count',  parsedData ); // Pass the entire parsedData
      } else if (command === 'update_unread_count'){ //handle update_unread_count
        this.notifyCallbacks('update_unread_count', parsedData);
      }
    } catch (err) {
      console.error('Error parsing message:', err);
      this.notifyCallbacks('error', { message: 'Invalid message format' });
    }
  }

  initChatUser(username) {
    this.sendMessage({ command: 'init_chat', username });
  }

  fetchMessages({ user_id, context= null, before_timestamp = null }) {
    console.log('Fetching messages:', { user_id, context, before_timestamp });
    this.sendMessage({
      command: 'fetch_messages',
      user_id,
      context,
      before_timestamp: before_timestamp || moment().toISOString(),
    });
  }

  newChatMessage({ user_id, content, context= null, ip_address = null }) {
    this.sendMessage({
      command: 'new_message',
      user_id,
      content,
      context,
      ip_address
    });
  }

  fetchUnreadCount({ user_id, context = null }) {
    console.log('Fetching unread count for :', { user_id, context });
    this.sendMessage({
      command: 'fetch_unread_count',
      user_id,
      context
    });
  }

  updateUnreadCount({ user_id, message_id, context = null }) { // Add this method
    console.log('Updating unread count for :', { user_id, message_id, context });
    this.sendMessage({
      command: 'update_unread_count',
      user_id,
      message_id,
      context
    });
  }

  addCallbacks({ messagesCallback, newMessageCallback, errorCallback, initChatCallback, connectionStatusCallback, unreadCountCallback, updateUnreadCountCallback }) { // Added updateUnreadCountCallback
    if (messagesCallback) this.callbacks.messages.push(messagesCallback);
    if (newMessageCallback) this.callbacks.new_message.push(newMessageCallback);
    if (errorCallback) this.callbacks.error.push(errorCallback);
    if (initChatCallback) this.callbacks.init_chat.push(initChatCallback);
    if (connectionStatusCallback) this.callbacks.connection_status.push(connectionStatusCallback);
    if (unreadCountCallback) this.callbacks.unread_count.push(unreadCountCallback);
    if (updateUnreadCountCallback) this.callbacks.update_unread_count.push(updateUnreadCountCallback); // Added
  }

  removeCallbacks({ messagesCallback, newMessageCallback, errorCallback, initChatCallback, connectionStatusCallback, unreadCountCallback, updateUnreadCountCallback }) {  // Added updateUnreadCountCallback
    if (messagesCallback) {
      this.callbacks.messages = this.callbacks.messages.filter(cb => cb !== messagesCallback);
    }
    if (newMessageCallback) {
      this.callbacks.new_message = this.callbacks.new_message.filter(cb => cb !== newMessageCallback);
    }
    if (errorCallback) {
      this.callbacks.error = this.callbacks.error.filter(cb => cb !== errorCallback);
    }
    if (initChatCallback) {
      this.callbacks.init_chat = this.callbacks.init_chat.filter(cb => cb !== initChatCallback);
    }
    if (connectionStatusCallback) {
      this.callbacks.connection_status = this.callbacks.connection_status.filter(cb => cb !== connectionStatusCallback);
    }
    if (unreadCountCallback) {
      this.callbacks.unread_count = this.callbacks.unread_count.filter(cb => cb !== unreadCountCallback);
    }
    if (updateUnreadCountCallback) {
      this.callbacks.update_unread_count = this.callbacks.update_unread_count.filter(cb => cb !== updateUnreadCountCallback);
    }
  }

  notifyCallbacks(type, data) {
    if (this.callbacks[type]) {
      this.callbacks[type].forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error(`Error in ${type} callback:`, err);
        }
      });
    }
  }

  updateConnectionStatus(status) {
    this.connectionStatus = status;
    this.notifyCallbacks('connection_status', { status });
  }

  sendMessage(data) {
    console.log('Sending message:', data);
    if (!this.socketRef || this.socketRef.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not open');
      this.notifyCallbacks('error', { message: 'WebSocket is not connected' });
      return;
    }
    try {
      this.socketRef.send(JSON.stringify(data));
    } catch (err) {
      console.error('Error sending message:', err);
      this.notifyCallbacks('error', { message: 'Failed to send message' });
    }
  }

  state() {
    return this.connectionStatus;
  }

  waitForSocketConnection(callback) {
    if (!this.socketRef) {
      setTimeout(() => this.waitForSocketConnection(callback), 100);
      return;
    }
    if (this.socketRef.readyState === WebSocket.OPEN) {
      console.log('Connection is made');
        this.updateConnectionStatus('connected');
      if (callback) callback();
      return;
    }
    setTimeout(() => this.waitForSocketConnection(callback), 100);
  }

  disconnect() {
    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout);
      this._reconnectTimeout = null;
    }
    if (this.socketRef) {
      this.socketRef.close();
      this.socketRef = null;
      this.updateConnectionStatus('disconnected');
    }
  }
}

const WebSocketInstance = WebSocketService.getInstance();
export default WebSocketInstance;
